<template>
    <div class="relative flex pt-3 sm:pt-5 px-1 md:px-3">
        <div class="w-full">
            <div
                @click="toggleShowGiftCard"
                style="height: 50px"
                class="email-input flex items-center justify-between p-2 pl-0 rounded cursor-pointer"
            >
                <span class="text-itembody text-sm font-black select-none">
                    {{
                        $t('giftCardBox.addGiftCardReference', {
                            provider: providerName
                        })
                    }}
                </span>
                <span class="text-itembody font-medium text-sm">
                    <dropdown-icon
                        class="transition duration-300"
                        :class="{ 'rotate-180': showGiftCard }"
                    />
                </span>
            </div>

            <div v-if="showGiftCard" class="relative flex mt-1">
                <div class="relative w-2/3 md:pr-2">
                    <input
                        type="text"
                        name="gift-card-code"
                        v-model.trim="giftCardCode"
                        id="gift-card-code"
                        :disabled="giftCardApplied || isLoading"
                        style="height: 50px"
                        :class="{ 'shadow-outline-red border-red-300': error }"
                        class="email-input disabled:opacity-50 block pt-3 px-4 w-full text-sm bg-secondary border rounded shadow-sm"
                        required
                        @blur="error = null"
                        @input="error = null"
                        @keyup.enter.prevent="handleCheck"
                    />
                    <label
                        for="gift-card-code"
                        class="email-input-label absolute pt-1 px-4 text-sm rounded pointer-events-none select-none"
                    >
                        {{
                            $t('giftCardBox.giftCard', {
                                provider: providerName
                            })
                        }}
                    </label>
                </div>
                <div class="flex pl-2 w-1/3">
                    <base-button
                        size="sm"
                        v-if="!giftCardApplied"
                        class="p-1 py-3"
                        button-text="Check"
                        rounded="rounded-md"
                        :class="{ spinner: isLoading }"
                        :disabled="isLoading || !giftCardCode || !!error"
                        @clicked="handleCheck"
                    />
                    <base-button
                        v-else
                        size="sm"
                        class="p-1 py-3"
                        button-text="Apply"
                        rounded="rounded-md"
                        :class="{ spinner: isLoading }"
                        :disabled="isLoading"
                        @clicked="handleApply"
                    />
                </div>
            </div>

            <div
                v-if="giftCardBalance > 0 && !appliedAmount"
                class="bg-green-50 ring-1 rounded-md ring-green-100 px-2 py-1 mt-3"
            >
                <span class="font-medium text-green-800 text-sm select-none">
                    {{
                        $t('giftCardBox.currentBalance', {
                            amount: (giftCardBalance / 100) | currency
                        })
                    }}
                </span>
            </div>

            <div
                v-if="appliedAmount"
                class="bg-green-50 ring-1 ring-green-100 px-2 py-1 mt-3"
            >
                <span class="font-medium text-green-800 text-sm select-none">
                    {{
                        $t('giftCardBox.appliedAmount', {
                            amount: (appliedAmount / 100) | currency
                        })
                    }}
                    <button
                        class="underline font-semibold text-sm text-green-800"
                        @click="remove"
                        >{{ $t('giftCardBox.remove') }}</button
                    >
                </span>
            </div>

            <span v-if="error" class="py-2 text-red-600 text-xs">{{
                error
            }}</span>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';

import DropdownIcon from '@/components/icons/DropdownIcon';
import * as giftcardService from '@/services/giftcard.service';

export default {
    name: 'GiftCardBox',
    components: { DropdownIcon },
    data() {
        return {
            giftCardCode: null,
            error: null,
            isLoading: false,
            showGiftCard: false
        };
    },
    computed: {
        ...mapGetters({
            amountSelectedToPay: 'bill/amountSelected',
            billId: 'bill/id',
            venueValue: 'venue/venueValue',
            giftCardBalance: 'giftCard/giftCardBalance',
            giftCardApplied: 'giftCard/giftCardApplied',
            getCartTotalWithDiscount: 'cart/cartTotalWithDiscount'
        }),
        ...mapState({
            appliedAmount: state => state.giftCard.giftCardPayAmount
        }),
        providerName() {
            return giftcardService.formatProviderName(this.giftCardProvider);
        },
        venueId() {
            return this.venueValue('id');
        },
        accountId() {
            return this.venueValue('accountId');
        },
        giftCardProvider() {
            return this.venueValue('giftCardProvider');
        }
    },
    methods: {
        ...mapActions({
            getGiftCardBalance: 'giftCard/checkGiftCardBalance',
            redeemGiftCard: 'giftCard/redeemGiftCard',
            setGiftCardApplied: 'giftCard/setGiftCardApplied',
            removeGiftCard: 'giftCard/removeGiftCard',
            setGiftCardPaymentAmount: 'cart/setGiftCardPaymentAmount',
            cartTotalWithDiscount: 'cart/cartTotalWithDiscount'
        }),
        toggleShowGiftCard() {
            this.showGiftCard = !this.showGiftCard;
        },
        async handleCheck() {
            const validationError = giftcardService.validateGiftCardCode(
                this.giftCardCode
            );

            if (validationError) {
                this.error = validationError;
                return;
            }

            this.isLoading = true;
            this.error = null;

            try {
                const params = {
                    giftCardCode: this.giftCardCode,
                    venueId: this.venueId,
                    accountId: this.accountId
                };

                await this.getGiftCardBalance(params);

                if (this.giftCardBalance > 0) {
                    this.$store.commit('giftCard/setGiftCardApplied', true);
                } else {
                    this.error = this.$t('giftCardBox.errors.codeNotValid');
                }
            } catch (error) {
                this.error =
                    error.response.data.message ||
                    this.$t('giftCardBox.errors.codeNotValid');
            } finally {
                this.isLoading = false;
            }
        },
        async handleApply() {
            try {
                if (this.giftCardProvider === 'piggy') {
                    this.$modal.show('pay-with-piggy-gift-card', {
                        giftCardBalance: this.giftCardBalance
                    });
                }

                if (this.giftCardProvider === 'giftpro') {
                    if (this.billId) {
                        const payload = {
                            billId: this.billId,
                            amount: this.amountSelectedToPay,
                            venueId: this.venueId,
                            giftCardCode: this.giftCardCode
                        };

                        await this.redeemGiftCard(payload);
                    } else {
                        const giftCardPaymentAmount =
                            giftcardService.calculateGiftCardPaymentAmount(
                                this.giftCardBalance,
                                this.getCartTotalWithDiscount
                            );

                        const newCartTotal =
                            giftcardService.calculateRemainingCartTotal(
                                this.getCartTotalWithDiscount,
                                giftCardPaymentAmount
                            );

                        this.$store.commit(
                            'giftCard/setGiftCardPayAmount',
                            giftCardPaymentAmount
                        );
                        this.$store.commit(
                            'cart/setTotalWithDiscount',
                            newCartTotal
                        );
                    }
                }
            } catch (error) {
                this.error = this.$t('giftCardBox.failedToApply');
            }
        },
        remove() {
            this.giftCardCode = null;
            this.error = null;
            this.removeGiftCard();
            this.cartTotalWithDiscount();
        }
    },
    beforeDestroy() {
        this.remove();
    }
};
</script>

<style scoped>
.spinner {
    color: white !important;
    opacity: 0.9;
}
.spinner:after {
    left: auto;
    right: 15px;
}
</style>
